import { useState, useEffect } from 'preact/hooks';
import { AnimatePresence } from 'framer-motion';
import {
  GOLD, GOLD_TEXT, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, PANEL_BG, ACCENT_GOLD,
  BEVELED_BTN, POPOVER_STYLE,
} from '../lib/medievalTheme';
import { CoinIcon, ShardIcon, KeystoneIcon, IconSkillTree } from './ui/icons';
import { Tooltip } from './ui/tooltip';
import { levelFromXp } from '../utils/arena/profileDefaults';
import { getAvailableSkillPoints, countSpent } from '../utils/arena/skills';
import AudioMixerPopover from './AudioMixerPopover';
import ClassCurrenciesButton from './ClassCurrenciesButton';
import LaboratoryButton from './laboratory/LaboratoryButton';
import { getCustomMusicState, subscribeCustomMusic } from '../utils/arena/customMusicManager';
import {
  canPreviewRoles, availablePreviewRoles, getRolePreview, setRolePreview, onRolePreviewChange,
} from '../utils/staffAccess';
import { isDevServerBuild } from '../utils/updateChannel';
import { t } from '../localization/t';
import { isFreePlay } from '../utils/freePlay';

const ENVELOPE_PATH = 'M4 5h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2zM2.5 6.5 12 13l9.5-6.5';

/**
 * Unified header bar used across all screens.
 *
 * Layout: [left (flex, grows)] [right (fixed-width slot)]
 *
 * The right side (gold) occupies a fixed-width slot so it never moves
 * regardless of what the left side contains. Friends & Mail live as a
 * single icon button at the tail of the right cluster. Clicking it
 * opens the app-level social modal. The left side is passed as
 * `children` and can contain any screen-specific content.
 *
 * `centerContent` renders absolutely-centered over the header so it
 * doesn't displace the left/right clusters. Screens (e.g. the Store)
 * can drop their top-level tab bar into the header without shifting
 * the back button or the currencies off-position.
 */
export default function AppHeader({
  children,
  className,
  centerContent,
  profile,
  onToggleMailbox,
  mailboxUnreadCount,
  mailboxOpen,
  onOpenSkillTree,
  noBorder,
  // Legacy props. Kept for backward compat but no longer rendered
  mailboxDropdown,
  onToggleFriends,
  friendListData,
}) {
  // Free play has no currencies, classes or skill trees.
  const freePlay = isFreePlay();
  return (
    <div
      className={className ? `relative z-20 flex items-center px-6 py-2.5 shrink-0 ${className}` : 'relative z-20 flex items-center px-6 py-2.5 shrink-0'}
      style={{
        borderBottom: noBorder ? 'none' : `1px solid ${GOLD} 0.15)`,
        background: 'rgba(12, 10, 8, 0.92)',
        backdropFilter: 'blur(8px)',
              }}
    >
      {/* Left side. Screen-specific content, grows to fill available space */}
      <div className="flex items-center gap-6 flex-1 min-w-0">
        {children}
      </div>

      {/* Centered slot. Floats over the header so the left/right
          clusters keep their natural flow. `pointer-events-none` on the
          wrapper lets clicks pass through empty areas to anything
          underneath; the inner wrapper re-enables events for the
          content itself. */}
      {centerContent ? (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="pointer-events-auto">
            {centerContent}
          </div>
        </div>
      ) : null}

      {/* Right side. Uniform 24 px rhythm to match the header's px-6.
          `gap-6` between every right-side element (shards → gold →
          friends → mail) lines up exactly with the 24 px padding on
          the header edges, so the spacing between currencies matches
          the spacing between buttons AND the distance from the last
          button to the right edge. No fixed-width slots either: the
          currencies flow at their natural size, giving them the same
          rhythm as the friends/mail buttons next to them. */}
      <div className="flex items-center gap-6 shrink-0 ml-auto pl-6">
        {/* Currency cluster. Icon + count only. The medieval Tooltip
            on hover names each currency and explains where it's earned
            and spent, so the header stays compact without losing
            discoverability. Currencies hide entirely when the field is
            missing (rather than rendering "0") only because the value
            could be a brand-new field the server hasn't backfilled
            yet. `!= null` keeps a real zero balance visible. */}
        {!freePlay && profile?.arcanaShards != null && (
          <Tooltip
            side="bottom"
            maxWidth="16cqw"
            content={
              <>
                <div className="font-bold mb-1" style={{ color: '#7dd3fc', fontSize: '0.625cqw' }}>{t('Arcana')}</div>
                <div className="font-normal" style={{ lineHeight: 1.5 }}>{t('Earned from matches and the season pass. Used to craft cards at the spellcraft workbench.')}</div>
              </>
            }
          >
            <div className="flex items-center gap-1.5 cursor-help">
              <ShardIcon size={16} />
              <span className="text-lg font-bold tabular-nums" style={{ color: '#7dd3fc', textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>{profile.arcanaShards}</span>
            </div>
          </Tooltip>
        )}

        {!freePlay && profile?.keystones != null && (
          <Tooltip
            side="bottom"
            maxWidth="16cqw"
            content={
              <>
                <div className="font-bold mb-1" style={{ color: '#c9a86a', fontSize: '0.625cqw' }}>{t('Keystones')}</div>
                <div className="font-normal" style={{ lineHeight: 1.5 }}>{t('Granted for completing achievements. Spend one to enter a draft event in lieu of the gold cost.')}</div>
              </>
            }
          >
            <div className="flex items-center gap-1.5 cursor-help">
              <KeystoneIcon size={16} />
              <span className="text-lg font-bold tabular-nums" style={{ color: '#c9a86a', textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>{profile.keystones}</span>
            </div>
          </Tooltip>
        )}

        {!freePlay && profile?.coins != null && (
          <Tooltip
            side="bottom"
            maxWidth="16cqw"
            content={
              <>
                <div className="font-bold mb-1" style={{ color: '#f0d060', fontSize: '0.625cqw' }}>{t('Gold')}</div>
                <div className="font-normal" style={{ lineHeight: 1.5 }}>{t('Earned from matches and the season pass. Spend on draft entries or auction-house dealings.')}</div>
              </>
            }
          >
            <div className="flex items-center gap-1.5 cursor-help">
              <CoinIcon size={16} />
              <span className="text-lg font-bold tabular-nums" style={{ color: '#f0d060', textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>{profile.coins}</span>
            </div>
          </Tooltip>
        )}

        {/* Audio mixer. Ambience + custom music library. Same popover
            as the in-game one, so players can manage their music from
            anywhere. Sits left of the queue/invites cluster so the
            right-hand band reads as currencies → music → queues +
            invites → progression → social. */}
        <AudioMixerButton />

        {/* The class's meta currencies, behind its major-node ornament.
            Sits between music and the tree it belongs to. */}
        {freePlay ? null : <ClassCurrenciesButton profile={profile} />}

        {/* An Alchemist's laboratory door, beside the plate it feeds. */}
        {freePlay ? null : <LaboratoryButton profile={profile} />}

        {/* Skill tree. Meta-progression entry. Always visible alongside
            the social button so the player can open the Skill Tree from
            any screen. If the parent passes `onOpenSkillTree` we use it
            directly; otherwise we dispatch a window event that app.jsx
            catches so we don't have to thread the callback through every
            AppHeader caller. */}
        {profile && !freePlay ? (() => {
          // SP availability now comes from the server-claimed
          // grantedSkillPoints column (see Progression screen). Pass
          // the profile so the helper resolves it consistently across
          // the snapshot's nested `progression` block.
          const available = getAvailableSkillPoints(profile);
          const spent = countSpent(profile.skills || { pointsSpent: [] });
          const unspent = Math.max(0, available - spent);
          const handleOpen = () => {
            if (typeof onOpenSkillTree === 'function') {
              onOpenSkillTree();
            } else {
              window.dispatchEvent(new CustomEvent('vh:open-skill-tree'));
            }
          };
          return (
            <button
              type="button"
              data-tutorial="skill-tree-header"
              onClick={handleOpen}
              className="relative flex items-center justify-center w-9 h-9 rounded-full transition-all cursor-pointer"
              style={{
                background: 'rgba(14,12,8,0.55)',
                border: `1.5px solid ${GOLD} 0.25)`,
                boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `${GOLD} 0.12)`;
                e.currentTarget.style.borderColor = `${GOLD} 0.5)`;
                e.currentTarget.style.boxShadow = `0 0 10px ${GOLD} 0.18), 0 2px 6px rgba(0,0,0,0.4)`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(14,12,8,0.55)';
                e.currentTarget.style.borderColor = `${GOLD} 0.25)`;
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.35)';
              }}
              title={unspent > 0
                ? (unspent === 1 ? t('Mastery. 1 unspent skill point') : t('Mastery. {n} unspent skill points', { n: unspent }))
                : t('Mastery (skill tree)')}
            >
              <IconSkillTree size={16} style={{ color: TEXT_BODY }} />
              {unspent > 0 ? (
                <div
                  className="absolute -top-1 -right-1 min-w-[1cqw] h-[1cqw] rounded-full flex items-center justify-center px-1"
                  style={{ background: ACCENT_GOLD, color: '#0e0a06', fontSize: '0.5625cqw', fontWeight: 700 }}
                >
                  {unspent > 99 ? '99+' : unspent}
                </div>
              ) : null}
            </button>
          );
        })() : null}

        {/* Social hub. Messages, the Tavern and notifications (invites,
            activity, mail, news) all live behind this one button. */}
        {onToggleMailbox ? (
          <button
            type="button"
            data-tutorial="social-hub"
            onClick={onToggleMailbox}
            className="relative flex items-center justify-center w-9 h-9 rounded-full transition-all cursor-pointer"
            style={{
              background: mailboxOpen ? `${GOLD} 0.12)` : 'rgba(14,12,8,0.55)',
              border: `1.5px solid ${mailboxOpen ? `${GOLD} 0.5)` : `${GOLD} 0.25)`}`,
              boxShadow: mailboxOpen ? `0 0 10px ${GOLD} 0.15)` : '0 2px 6px rgba(0,0,0,0.35)',
            }}
            title={t('Social Hub (O)')}
          >
            <svg
              className="pointer-events-none"
              style={{ width: '1cqw', height: '1cqw' }}
              viewBox="0 0 24 24" fill="none"
              stroke={mailboxOpen ? ACCENT_GOLD : TEXT_BODY}
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            >
              <path d={ENVELOPE_PATH} />
            </svg>
            {mailboxUnreadCount > 0 && !mailboxOpen ? (
              <div
                className="pointer-events-none absolute -top-1 -right-1 min-w-[1cqw] h-[1cqw] rounded-full flex items-center justify-center px-1"
                style={{ background: ACCENT_GOLD, color: '#0e0a06', fontSize: '0.5625cqw', fontWeight: 700 }}
              >
                {mailboxUnreadCount > 99 ? '99+' : mailboxUnreadCount}
              </div>
            ) : null}
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Button that anchors the AudioMixerPopover. Highlights when a custom
 * music track is playing so players can see at a glance that their
 * library is active. Click-outside dismisses; click again closes.
 */
function AudioMixerButton() {
  const [open, setOpen] = useState(false);
  const [cm, setCm] = useState(getCustomMusicState());

  useEffect(() => {
    return subscribeCustomMusic(() => setCm(getCustomMusicState()));
  }, []);

  const active = cm.isPlaying;

  return (
    <div className="relative">
      <button
        type="button"
        data-tutorial="music"
        onClick={() => setOpen(!open)}
        className="relative flex items-center justify-center w-9 h-9 rounded-full transition-all cursor-pointer"
        style={{
          background: open || active ? `${GOLD} 0.12)` : 'rgba(14,12,8,0.55)',
          border: `1.5px solid ${open || active ? `${GOLD} 0.5)` : `${GOLD} 0.25)`}`,
          boxShadow: open || active ? `0 0 10px ${GOLD} 0.15)` : '0 2px 6px rgba(0,0,0,0.35)',
        }}
        title={t('Music & Ambience')}
      >
        <svg
          viewBox="0 0 24 24"
          style={{ width: '1cqw', height: '1cqw' }}
          fill="none"
          stroke={active ? ACCENT_GOLD : TEXT_BODY}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M9 18V5l12-2v13" />
          <circle cx="6" cy="18" r="3" />
          <circle cx="18" cy="16" r="3" />
        </svg>
      </button>
      {/* No backdrop overlay here: the header sets `backdrop-filter`, which
          makes it the containing block for `position: fixed` children, so a
          "fixed inset-0" shield only ever covered the header bar. It blocked
          the neighbouring header buttons while failing to catch clicks
          anywhere else. AudioMixerPopover closes itself on any outside click,
          and this button's own toggle handles clicking it a second time. */}
      <AnimatePresence>
        {open ? (
          <AudioMixerPopover
            key="mixer"
            positionClassName="absolute right-0 top-full mt-2 z-20"
            onClose={() => setOpen(false)}
          />
        ) : null}
      </AnimatePresence>
    </div>
  );
}

const ROLE_LABELS = { admin: 'Admin', moderator: 'Moderator', qa: 'QA Tester' };

/**
 * Dev-only role preview switcher.
 *
 * Renders the whole app as a moderator or QA tester sees it. The hub's
 * staff button, the panel's title, and every capability-gated tab and
 * section. So the three roles can be checked without three accounts.
 *
 * Gated twice, both halves required: an unpackaged build pointed at the
 * local server (isDevServerBuild), and a profile carrying the server's
 * capability matrix, which only owners are sent. Nothing here changes
 * what the server will actually let the account do. RequireCap runs
 * against the real role. So a preview is a view, not a demotion.
 */
export function RolePreviewButton({ profile }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(getRolePreview());

  useEffect(() => onRolePreviewChange(setPreview), []);

  if (!isDevServerBuild() || !canPreviewRoles(profile)) return null;

  const realRole = profile.staffRole || 'admin';
  const shown = preview || realRole;
  // Amber only while standing in someone else's shoes, so the header
  // reads as normal when the preview is off.
  const impersonating = shown !== realRole;

  return (
    <div className="relative">
      {/* Shape mirrors the hub's Admin button it sits beside. Same
          padding, weight and corner radius. So the pair reads as one
          control group. Amber instead of red keeps them distinct. */}
      <button
        type="button"
        className="inline-flex items-center gap-2 px-3 py-1 text-[0.625cqw] font-bold transition-transform hover:scale-[1.05] active:scale-[0.96]"
        style={{
          background: impersonating
            ? 'linear-gradient(180deg, rgba(240,180,90,0.18) 0%, rgba(160,110,40,0.12) 100%)'
            : 'linear-gradient(180deg, rgba(212,168,67,0.12) 0%, rgba(120,90,30,0.08) 100%)',
          border: `1px solid ${impersonating ? 'rgba(240,180,90,0.6)' : `${GOLD} 0.3)`}`,
          borderRadius: 'var(--radius-sm)',
          color: impersonating ? '#f0b45a' : TEXT_BODY,
          textShadow: '0 1px 2px rgba(0,0,0,0.5)',
        }}
        title={impersonating
          ? `Previewing the panel as ${ROLE_LABELS[shown] || shown}. Your real role is ${ROLE_LABELS[realRole] || realRole}`
          : 'Preview the panel as another staff role (dev server only)'}
        onClick={() => setOpen(!open)}
      >
        <span>{ROLE_LABELS[shown] || shown}</span>
        <span className="text-[0.5cqw] leading-none" style={{ color: TEXT_MUTED }}>▼</span>
      </button>
      {open ? (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full mt-2 z-20 p-3 min-w-[13cqw]" style={POPOVER_STYLE}>
            <div className="text-[0.625cqw] font-semibold mb-2" style={{ color: `${GOLD} 0.55)` }}>
              View the panel as
            </div>
            {availablePreviewRoles(profile).map((role) => {
              const isShown = role === shown;
              const caps = profile.capabilityMatrix[role] || [];
              return (
                <button
                  key={role}
                  type="button"
                  // Selecting the real role clears the preview rather
                  // than pinning it, so "back to normal" is the same
                  // click as "show me admin".
                  onClick={() => { setRolePreview(role === realRole ? null : role); setOpen(false); }}
                  className="flex w-full items-center gap-2 px-2 py-1.5 mb-1 last:mb-0 cursor-pointer transition-colors text-left rounded"
                  style={{
                    background: isShown ? `${GOLD} 0.12)` : 'transparent',
                    border: `1px solid ${isShown ? `${GOLD} 0.45)` : 'transparent'}`,
                  }}
                  onMouseEnter={(e) => { if (!isShown) e.currentTarget.style.background = `${GOLD} 0.06)`; }}
                  onMouseLeave={(e) => { if (!isShown) e.currentTarget.style.background = 'transparent'; }}
                >
                  <span className="text-[0.6875cqw] font-semibold" style={{ color: isShown ? ACCENT_GOLD : TEXT_BODY }}>
                    {ROLE_LABELS[role] || role}
                  </span>
                  {role === realRole ? (
                    <span className="text-[0.5625cqw]" style={{ color: TEXT_MUTED }}>(yours)</span>
                  ) : null}
                  <span className="ml-auto text-[0.5625cqw] tabular-nums" style={{ color: TEXT_MUTED }}>
                    {caps.length} caps
                  </span>
                </button>
              );
            })}
            <p className="mt-2 text-[0.5625cqw] leading-relaxed" style={{ color: TEXT_MUTED }}>
              Display only. The server still checks your real role, and a reload drops the preview.
            </p>
          </div>
        </>
      ) : null}
    </div>
  );
}

// Invite popover. `codes` is the authoritative list of every code the
// player owns; falls back to `code` + `redeemed` for older
// /profile/me payloads that haven't been re-fetched yet. Each player
// is kept topped up to three unused codes server-side, so the popover
// usually shows three actionable codes plus any older redeemed ones
// the player already gave out.
export function InviteButton({ codes, code, redeemed = false }) {
  const [open, setOpen] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);

  // Normalise to a list. Old single-code payloads still work via the
  // fallback. Filter out empty entries defensively.
  const list = Array.isArray(codes) && codes.length > 0
    ? codes.filter((c) => c && c.code)
    : (code ? [{ code, redeemed: !!redeemed }] : []);

  const unused = list.filter((c) => !c.redeemed);
  const used = list.filter((c) => c.redeemed);
  const allUsed = list.length > 0 && unused.length === 0;
  const hasAny = list.length > 0;

  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const el = document.createElement('textarea');
      el.value = value;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopiedCode(value);
    setTimeout(() => setCopiedCode((cur) => (cur === value ? null : cur)), 2000);
  };

  const triggerColor = !hasAny || allUsed ? TEXT_MUTED : ACCENT_GOLD;
  const triggerTitle = !hasAny
    ? t('No invite codes yet')
    : allUsed
    ? t('All invite codes already redeemed')
    : unused.length === 1
    ? t('1 unused invite code. Click to view')
    : t('{n} unused invite codes. Click to view', { n: unused.length });

  return (
    <div className="relative">
      <button
        type="button"
        className="relative inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold transition-all duration-200 hover:scale-[1.03] active:scale-[0.98]"
        style={{
          ...BEVELED_BTN,
          color: triggerColor,
          opacity: allUsed ? 0.6 : 1,
          filter: allUsed ? 'grayscale(0.75)' : 'none',
        }}
        title={triggerTitle}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${GOLD} ${allUsed ? '0.2' : '0.5'})`; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = `${GOLD} 0.3)`; }}
        onClick={() => setOpen(!open)}
      >
        <span>{t('Invite')}</span>
        {hasAny && !allUsed ? (
          <span
            className="inline-flex items-center justify-center text-[0.5625cqw] font-bold leading-none rounded-full"
            style={{
              minWidth: '1cqw',
              height: '1cqw',
              padding: '0 4px',
              background: ACCENT_GOLD,
              color: '#1a120a',
              textShadow: 'none',
            }}
          >
            {unused.length}
          </span>
        ) : allUsed ? (
          <span className="text-[0.5625cqw] tracking-widest leading-none" style={{ color: TEXT_MUTED }}>
            {t('Used')}
          </span>
        ) : null}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div
            className="absolute left-0 top-full mt-2 z-20 p-4 min-w-[16.25cqw]"
            style={POPOVER_STYLE}
          >
            <div className="text-[0.625cqw] font-semibold mb-3" style={{ color: `${GOLD} 0.55)` }}>
              {allUsed
                ? t('Your Invite Codes (all used)')
                : t('Your Invite Codes ({n} unused)', { n: unused.length })}
            </div>

            {/* Unused codes. Primary affordance, copy button enabled. */}
            {unused.map((entry) => (
              <div key={entry.code} className="flex items-center gap-2 mb-2">
                <span
                  className="flex-1 text-center text-base font-bold font-mono tracking-[0.18em] py-1.5 rounded"
                  style={{
                    color: ACCENT_GOLD,
                    background: 'rgba(0,0,0,0.3)',
                    border: `1px solid ${GOLD} 0.2)`,
                    textShadow: `0 0 12px ${GOLD} 0.3)`,
                  }}
                >
                  {entry.code}
                </span>
                <button
                  type="button"
                  className="px-3 py-1.5 text-[0.625cqw] font-semibold transition-all cursor-pointer"
                  style={{
                    ...BEVELED_BTN,
                    borderRadius: 'var(--radius-md)',
                    color: copiedCode === entry.code ? '#6ab04c' : TEXT_BODY,
                  }}
                  onClick={() => copy(entry.code)}
                >
                  {copiedCode === entry.code ? t('Copied!') : t('Copy')}
                </button>
              </div>
            ))}

            {/* Already-redeemed codes. Kept visible (struck through) so
                the player can see who they invited, but the copy button
                is removed since the code no longer works. */}
            {used.length > 0 ? (
              <>
                <div className="mt-3 mb-1 text-[0.5625cqw] font-semibold" style={{ color: TEXT_MUTED }}>
                  {t('Already used')}
                </div>
                {used.map((entry) => (
                  <div key={entry.code} className="flex items-center gap-2 mb-1">
                    <span
                      className="flex-1 text-center text-sm font-mono tracking-[0.16em] py-1 rounded"
                      style={{
                        color: TEXT_MUTED,
                        background: 'rgba(0,0,0,0.2)',
                        border: `1px solid ${GOLD} 0.1)`,
                        textDecoration: 'line-through',
                        filter: 'grayscale(0.8)',
                      }}
                    >
                      {entry.code}
                    </span>
                  </div>
                ))}
              </>
            ) : null}

            <p className="mt-3 text-[0.625cqw] leading-relaxed" style={{ color: TEXT_MUTED }}>
              {!hasAny
                ? t('You don’t have any invite codes yet. Ask an admin to seed you some.')
                : allUsed
                ? t('You’ve used all three of your invite codes. We may hand out another batch in a future update. For now, that’s it.')
                : t('Share these codes with friends to invite them. Each code works once. You start with three; once they’re spent, no more will arrive automatically.')}
            </p>
          </div>
        </>
      )}
    </div>
  );
}
